import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '@core/api/api.tokens';
import { suppressErrorToast } from '@core/errors/error.http-context';
import { toDateOnly, toTimeOnly } from '@core/utils/date-time.utils';
import { DateTime } from 'luxon';
import { BehaviorSubject, map, Observable, of, switchMap, take, tap, throwError } from 'rxjs';
import { WorkTimeHistoryEntry, WorkTimePagination, WorkTimeQuery, WorkTimeSession } from './work-time.types';

@Injectable({
    providedIn: 'root',
})
export class WorkTimeService {
    private _apiUrl = inject(API_BASE_URL).replace(/\/+$/, '') + '/';

    private _workTimeSessions: BehaviorSubject<WorkTimeSession[] | null> = new BehaviorSubject(null);
    private _workTimeSession: BehaviorSubject<WorkTimeSession | null> = new BehaviorSubject(null);
    private _pagination: BehaviorSubject<WorkTimePagination | null> = new BehaviorSubject(null);

    constructor(private _httpClient: HttpClient) {}

    get pagination$(): Observable<WorkTimePagination> {
        return this._pagination.asObservable();
    }
    get workTimeSessions$(): Observable<WorkTimeSession[]> {
        return this._workTimeSessions.asObservable();
    }
    get workTimeSession$(): Observable<WorkTimeSession> {
        return this._workTimeSession.asObservable();
    }

    getWorkTimeSessions(
        query: WorkTimeQuery = {},
        updateState = true
    ): Observable<{ data: WorkTimeSession[]; pagination: WorkTimePagination }> {
        const {
            from = '2020-01-01',
            to = '2026-01-01',
            pageIndex = 0,
            size = 25,
            sort = 'date',
            order = 'asc',
            search = '',
            employeeIds,
            locationIds,
            departmentIds,
            status,
        } = query;

        let params = new HttpParams()
            .set('from', this.toIsoDate(from))
            .set('to', this.toIsoDate(to))
            .set('pageIndex', String(pageIndex))
            .set('size', String(size))
            .set('sort', sort ?? 'date')
            .set('order', order ?? 'asc');

        if (search) {
            params = params.set('search', search);
        }
        if (status) {
            params = params.set('status', status);
        }

        for (const id of employeeIds ?? []) {
            params = params.append('employeeIds', id);
        }

        for (const id of locationIds ?? []) {
            params = params.append('locationIds', id);
        }

        for (const id of departmentIds ?? []) {
            params = params.append('departmentIds', id);
        }

        return this._httpClient
            .get<{
                data: WorkTimeSession[];
                pagination: WorkTimePagination;
            }>(this._apiUrl + 'work-time/sessions', { params })
            .pipe(
                tap((response) => {
                    if (!updateState) return;
                    this._workTimeSessions.next(response.data);
                    this._pagination.next(response.pagination);
                })
            );
    }

    private toIsoDate(value: DateTime | string | null | undefined): string {
        return toDateOnly(value) ?? '';
    }

    private toWorkTimePayload(session: WorkTimeSession) {
        const minutesOrNull = (value: number | string | null | undefined): number | null =>
            value === '' || value === null || value === undefined ? null : Number(value);
        return {
            startTime: toTimeOnly(session.startTime),
            endTime: toTimeOnly(session.endTime),
            manualDurationMinutes: minutesOrNull(session.manualDurationMinutes),

            pauses: session.pauses?.map((pause) => ({
                ...(pause.id ? { id: pause.id } : {}),
                startTime: toTimeOnly(pause.startTime),
                endTime: toTimeOnly(pause.endTime),
                manualDurationMinutes: minutesOrNull(pause.manualDurationMinutes),
            })),
        };
    }

    getWorkTimeSessionById(id: string, updateState = true): Observable<WorkTimeSession> {
        return this._httpClient
            .get<WorkTimeSession>(
                `${this._apiUrl}work-time/sessions/${id}`,
                updateState ? {} : { context: suppressErrorToast() }
            )
            .pipe(
                tap((workTimeSession) => {
                    if (updateState) this._workTimeSession.next(workTimeSession);
                }),
                switchMap((workTimeSession) => {
                    if (!workTimeSession) {
                        return throwError(() => 'Could not found session with id of ' + id + '!');
                    }
                    return of(workTimeSession);
                })
            );
    }

    getHistory(id: string): Observable<WorkTimeHistoryEntry[]> {
        return this._httpClient.get<WorkTimeHistoryEntry[]>(
            `${this._apiUrl}work-time/sessions/${encodeURIComponent(id)}/history`
        );
    }

    createWorkTimeSession(session: WorkTimeSession): Observable<WorkTimeSession> {
        return this._httpClient.post<WorkTimeSession>(`${this._apiUrl}work-time/sessions`, {
            ...this.toWorkTimePayload(session),
            employeeId: session.employeeId,
            date: toDateOnly(session.date),
        });
    }

    updateWorkTimeSession(id: string, workTimeSession: WorkTimeSession): Observable<WorkTimeSession> {
        return this._httpClient
            .patch<WorkTimeSession | null>(
                `${this._apiUrl}work-time/sessions/${id}`,
                this.toWorkTimePayload(workTimeSession)
            )
            .pipe(
                map((updatedWorkTimeSession) => {
                    const currentSession = this._workTimeSession.value;
                    return {
                        ...(currentSession?.id === id ? currentSession : {}),
                        ...workTimeSession,
                        ...(updatedWorkTimeSession ?? {}),
                    } as WorkTimeSession;
                }),
                tap((updatedWorkTimeSession) => {
                    // Details omit the list's totalPauseMinutes and employee/organisation fields.
                    // The caller refreshes the list using its own response contract.
                    this._workTimeSession.next(updatedWorkTimeSession);
                })
            );
    }

    confirmWorkTimeSession(id: string): Observable<WorkTimeSession> {
        return this._httpClient
            .post<WorkTimeSession>(`${this._apiUrl}work-time/sessions/${encodeURIComponent(id)}/confirm`, null)
            .pipe(tap((session) => this._workTimeSession.next(session)));
    }

    deleteWorkTimeSession(id: string): Observable<boolean> {
        return this.workTimeSessions$.pipe(
            take(1),
            switchMap((products) =>
                this._httpClient.delete(`${this._apiUrl}work-time/sessions/${id}`).pipe(
                    map((isDeleted: boolean) => {
                        if (isDeleted === false) return false;
                        // Find the index of the deleted product
                        this._workTimeSessions.next((products ?? []).filter((item) => item.id !== id));

                        // Return the deleted status
                        return true;
                    })
                )
            )
        );
    }

    deleteWorkTimeSessionPause(id: string, updateState = true): Observable<boolean> {
        return this.workTimeSession$.pipe(
            take(1),
            switchMap((workTimeSession) =>
                this._httpClient.delete(`${this._apiUrl}work-time/sessions/${workTimeSession.id}/pauses/${id}`).pipe(
                    map((isDeleted: boolean) => {
                        if (!updateState) return isDeleted;
                        // Find the index of the deleted product
                        const index = workTimeSession.pauses.findIndex((item) => item.id === id);

                        // Delete the product
                        if (index >= 0) workTimeSession.pauses.splice(index, 1);

                        // Update the products
                        this._workTimeSession.next(workTimeSession);

                        // Return the deleted status
                        return isDeleted;
                    })
                )
            )
        );
    }
}
